/**
 * Preço de um adicional ajustado SÓ nesta proposta (mode=orcamento): o fornecedor muda o valor e/ou a forma de
 * cobrança (por convidado ou valor fechado) sem alterar o cadastro do cardápio. Fica no item do carrinho e no
 * resumo do pedido (menu[].optionalOverrides) e vale para o cardápio, o carrinho, a loja, o checkout e o painel.
 */
export type OptionalPriceMode = 'per_guest' | 'fixed' | 'per_unit';
/** Quantidade escolhida pelo cliente em cada opção "por quantidade" (id do item -> unidades). */
export type OptionalQuantities = Record<string, number>;
export interface OptionalOverride { unit_cost: number; price_mode: OptionalPriceMode }
export type OptionalOverrides = Record<string, OptionalOverride>;

export interface OptionalLike {
  id?: string | null;
  unit_cost?: number | string | null;
  fixed_price?: number | string | null;
  optional_price_mode?: string | null;
  price_mode?: string | null;
  optional_min_qty?: number | null;
  optional_max_qty?: number | null;
  min_qty?: number | null;
  max_qty?: number | null;
}

/** Normaliza o que vem do banco/sessão (jsonb livre). Entradas inválidas são descartadas. */
export const normalizeOptionalOverrides = (value: unknown): OptionalOverrides => {
  let v = value;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch { return {}; }
  }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  const out: OptionalOverrides = {};
  Object.entries(v as Record<string, any>).forEach(([id, o]) => {
    const cost = Number(o?.unit_cost);
    if (!id || !Number.isFinite(cost) || cost < 0) return;
    out[id] = { unit_cost: cost, price_mode: o?.price_mode === 'fixed' ? 'fixed' : o?.price_mode === 'per_unit' ? 'per_unit' : 'per_guest' };
  });
  return out;
};

/** Valor e forma de cobrança efetivos do adicional (ajuste da proposta, senão o cadastro). */
export const effectiveOptionalPrice = (
  optional: OptionalLike,
  overrides?: OptionalOverrides | null,
): { cost: number; fixed: boolean; perUnit: boolean; overridden: boolean } => {
  const override = optional.id ? overrides?.[String(optional.id)] : undefined;
  if (override) {
    return {
      cost: override.unit_cost,
      fixed: override.price_mode === 'fixed',
      perUnit: override.price_mode === 'per_unit',
      overridden: true,
    };
  }
  const mode = optional.optional_price_mode ?? optional.price_mode;
  return {
    cost: Number(optional.unit_cost ?? optional.fixed_price ?? 0) || 0,
    fixed: mode === 'fixed',
    perUnit: mode === 'per_unit',
    overridden: false,
  };
};

/** true quando a opção é cobrada por quantidade (o cliente informa quantas unidades). */
export const isPerUnitOptional = (optional: OptionalLike, overrides?: OptionalOverrides | null): boolean =>
  effectiveOptionalPrice(optional, overrides).perUnit;

/**
 * Quantidade válida de uma opção por quantidade: inteiro entre o mínimo e o máximo do fornecedor, limitado ao total de
 * pessoas do evento (`maxPeople`, quando conhecido). Sem quantidade informada vale o mínimo (1 por padrão).
 */
export const clampOptionalQuantity = (
  optional: OptionalLike,
  quantity: unknown,
  maxPeople?: number | null,
): number => {
  const min = Math.max(1, Math.round(Number(optional.optional_min_qty ?? optional.min_qty) || 1));
  const supplierMax = Math.round(Number(optional.optional_max_qty ?? optional.max_qty));
  const max = supplierMax >= 1 ? supplierMax : maxPeople && maxPeople > 0 ? Math.round(maxPeople) : Infinity;
  const n = Math.round(Number(quantity));
  const wanted = Number.isFinite(n) && n >= 1 ? n : min;
  return Math.max(min, Math.min(wanted, Math.max(min, max)));
};

/** Normaliza as quantidades que vêm do banco/sessão (jsonb livre). */
export const normalizeOptionalQuantities = (value: unknown): OptionalQuantities => {
  let v = value;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch { return {}; }
  }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  const out: OptionalQuantities = {};
  Object.entries(v as Record<string, any>).forEach(([id, q]) => {
    const n = Math.round(Number(q));
    if (id && Number.isFinite(n) && n >= 1) out[id] = n;
  });
  return out;
};

/** Soma dos adicionais escolhidos, separada em por convidado e valor fechado (por quantidade = preço x unidades, fechado). */
export const splitOptionalCostsWithOverrides = (
  optionals: OptionalLike[] | null | undefined,
  selectedIds: string[] | null | undefined,
  overrides?: OptionalOverrides | null,
  quantities?: OptionalQuantities | null,
  maxPeople?: number | null,
): { perGuest: number; fixed: number } => {
  let perGuest = 0;
  let fixed = 0;
  const selected = new Set(selectedIds || []);
  (optionals || []).forEach((opt) => {
    if (!opt?.id || !selected.has(String(opt.id))) return;
    const price = effectiveOptionalPrice(opt, overrides);
    if (price.perUnit) fixed += price.cost * clampOptionalQuantity(opt, quantities?.[String(opt.id)], maxPeople);
    else if (price.fixed) fixed += price.cost;
    else perGuest += price.cost;
  });
  return { perGuest, fixed };
};

/** Nome da unidade válido: vazio vira "pessoa"; só número/símbolo vira "unidade". */
export const normalizeUnitLabel = (label: string | null | undefined): string => {
  const unit = (label || '').trim();
  if (!unit) return 'pessoa';
  // Só número ou símbolo (ex.: "1" digitado no campo Unidade por engano) não é uma unidade: vira "unidade" (8 unidades, não "8 1s").
  if (!/[A-Za-zÀ-ÿ]/.test(unit)) return 'unidade';
  return unit;
};

/** Unidade com o plural correto: 1 pessoa, 4 pessoas, 3 porções, 2 litros (sem informar, "pessoa"). */
export const formatUnitLabel = (label: string | null | undefined, quantity: number): string => {
  const unit = normalizeUnitLabel(label);
  if (quantity === 1) return unit;
  if (/ão$/i.test(unit)) return unit.replace(/ão$/i, 'ões');
  if (/[rz]$/i.test(unit)) return `${unit}es`;
  return /s$/i.test(unit) ? unit : `${unit}s`;
};
